// Seitenblätter-Fuß — EIN Baustein für alle blätterbaren Listen.
//
// ⚠️ Beim Zusammenlegen waren es FÜNF Verbraucher, nicht sechs: `TopicCategoryManagement.tsx` hat
// gar keine Blätter-Leiste und auch keinen `pagination`-Zustand — es lädt seine Themen-Kategorien
// am Stück. Wer dort eine Leiste einsetzt, baut eine neue Funktion, keine Zusammenlegung.
//
// ⚠️ Seit 2026-08-13 sind es ACHT: Alben, Interpreten und Playlists sind dazugekommen. Sie sind
// aber NICHT vom selben Schlag, und der Unterschied gehört hierher, weil er die Bedeutung der
// Zahlen an dieser Leiste ändert. Bei den ersten fünf kommen `seite`, `seitenAnzahl` und `gesamt`
// aus dem `pagination`-Umschlag der Antwort — sie sagen etwas über den Bestand auf dem Server. Bei
// den drei neuen kommen sie aus `useSeitenausschnitt`, also aus einer Rechnung über eine Liste,
// die vollständig im Speicher liegt: deren drei Admin-Endpunkte nehmen weder `page` noch `limit`
// entgegen. Für DIESEN Baustein ändert das nichts (er sieht ohnehin nur Zahlen); für die Frage
// „warum lädt die Seite bei 500 Alben immer noch 500 Zeilen" ist es die ganze Antwort. Sie steht
// im Kopf von `useSeitenausschnitt`.
//
// Gemessener Stand vor dem Zusammenlegen:
//
// | Ort                        | Rahmen                                     | Text                              | Knöpfe                | Gesperrt bei                    |
// |----------------------------|--------------------------------------------|-----------------------------------|-----------------------|---------------------------------|
// | Audio / Video / Benutzer   | `flex flex-col sm:flex-row … px-6 py-4 border-t` | `Seite X von Y · N Einträge` | `btn btn-outline`     | `page <= 1` / `page >= letzte`  |
// | MindBooks                  | dieselbe Kette                             | derselbe Text                     | `btn btn-outline`, aber OHNE `disabled:cursor-not-allowed` | `page <= 1` / `page >= letzte` |
// | Inhalte (ContentManagement)| `card-footer` + `flex … justify-between`   | `X bis Y von Z Einträgen`         | `btn btn-outline btn-sm` | `page === 1` / `page === letzte` |
//
// **Mehrheit gewinnt** (vier von fünf): der `border-t`-Rahmen, der Text „Seite X von Y" mit der
// gedämpften Anzahl dahinter, die normalgroßen Umriss-Knöpfe. `disabled:cursor-not-allowed` kommt
// mit, weil drei der vier es tragen — nur MindBooks nicht.
//
// **Was beim Angleichen verloren geht:**
// - *Inhalte* verliert seinen `card-footer`-Rahmen (der zieht `p-6 pt-0` statt `px-6 py-4` und
//   bringt keine Trennlinie mit), die kleineren Knöpfe (`btn-sm`) und vor allem seinen
//   Spannen-Text „1 bis 10 von 43 Einträgen". Der neue Text nennt dieselbe Gesamtzahl, aber nicht
//   mehr den sichtbaren Ausschnitt. Außerdem stand dort „Seite X von Y" ZWISCHEN den beiden
//   Knöpfen; künftig steht es links, wie bei den anderen vier.
// - *Inhalte* wechselt von `page === 1` auf `page <= 1`. Das ist keine Kosmetik: bei einem Wert
//   außerhalb des Bereichs — etwa nach einem Rücksprung oder einer Aktualisierung, die die Liste
//   verkürzt hat — ließ der Gleichheitsvergleich beide Knöpfe aktiv und schickte die Nutzerin auf
//   Seite 0.
//
// **Der Rücksprung aus einer leergewordenen Seite gehört NICHT hierher** (der Schutz aus
// `UserManagement.tsx`, der von einer leeren Seite 2 auf Seite 1 zurückgeht). Zwei Gründe, und der
// zweite ist zwingend:
// 1. Er reagiert nicht auf einen Klick, sondern auf ein LADE-ERGEBNIS (`daten.length === 0 &&
//    seite > 1`). Dieser Baustein sieht nur Zahlen, nie die Antwort der API.
// 2. Er müsste genau dann greifen, wenn die Liste leer ist — und dann ist dieser Baustein gar nicht
//    im Baum: er steht bei allen fünf Verbrauchern INNERHALB des „es gibt Einträge"-Zweigs, hinter
//    der Tabelle. Ein Rücksprung von hier aus könnte nie auslösen. Er bleibt in der Ladefunktion
//    des Verbrauchers, wo er heute auch steht.
//
// **Angesagt wird der Wechsel seit dem Umbau auch.** „Seite X von Y" war stummer Text in einem
// namenlosen `<div>`. Wer „Weiter" mit der Tastatur drückt, bleibt auf dem Knopf stehen: der Fokus
// wandert nicht, die Tabelle darüber tauscht ihren Inhalt aus, und ein Screenreader sagt dazu
// nichts. Zwei gängige Mittel beheben das, beide hier:
// - der Rahmen ist ein `<nav>` mit eigenem Namen — damit steht das Blättern in der Landmarken- und
//   Elementliste und ist anspringbar, statt als Knopf-Paar irgendwo hinter der Tabelle zu liegen;
// - der Zustandstext ist eine höfliche Live-Region (`aria-live="polite"`, `aria-atomic="true"`) —
//   sie wird nach dem Wechsel als Ganzes vorgelesen, ohne die Nutzerin zu unterbrechen.
//
// ⚠️ Bewusst `aria-live` + `aria-atomic` und NICHT `role="status"`, obwohl die Rolle genau diese
// beiden Eigenschaften mitbrächte: `Benachrichtigung.tsx` benutzt `role="status"` bereits für den
// Erfolgs-Balken. Zwei „status"-Rollen in derselben Ansicht machen die Ansage mehrdeutig — und
// jede Prüfung, die den Balken über `getByRole('status')` sucht, würde am Blätter-Fuß hängen
// bleiben. Für Hilfstechnik ist das Ergebnis identisch.
//
// Der fünfmal identische Rückruf für `onSeitenwechsel` steht als `seitenwechsel` in
// `filterZustand.ts` — nicht hier, weil `eslint-plugin-react-refresh` einer Datei mit
// Komponenten-Export keine zusätzlichen Wert-Exporte erlaubt.
import React from 'react';

interface SeitenblaetterFussProps {
  /** Aktuelle Seite, 1-basiert. */
  seite: number;
  /** Gesamtzahl der Seiten. Bei 1 oder weniger rendert der Baustein nichts. */
  seitenAnzahl: number;
  /** Gesamtzahl der Einträge über alle Seiten — die gedämpfte Zahl rechts im Text. */
  gesamt: number;
  /** Bekommt die Zielseite, bereits auf `1 … seitenAnzahl` begrenzt. */
  onSeitenwechsel: (seite: number) => void;
}

export const SeitenblaetterFuss: React.FC<SeitenblaetterFussProps> = ({
  seite,
  seitenAnzahl,
  gesamt,
  onSeitenwechsel,
}) => {
  // Die Bedingung `totalPages > 1` stand bei allen fünf Verbrauchern als `&&` davor. Sie hierher zu
  // ziehen ist der Punkt: eine fünfmal abgeschriebene Bedingung ist eine fünfmal falsch
  // abschreibbare Bedingung.
  if (seitenAnzahl <= 1) return null;

  // Begrenzung im Baustein, nicht beim Verbraucher: alle vier Mehrheits-Kopien trugen dasselbe
  // `Math.max(1, …)` / `Math.min(letzte, …)` von Hand, die Inhalte-Seite gar keins.
  const wechsle = (ziel: number) => onSeitenwechsel(Math.min(seitenAnzahl, Math.max(1, ziel)));

  return (
    <nav
      aria-label="Seitenblättern"
      className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-secondary-200"
    >
      <div className="text-sm text-secondary-600" aria-live="polite" aria-atomic="true">
        Seite {seite} von {seitenAnzahl}
        <span className="text-secondary-400"> · {gesamt} Einträge</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => wechsle(seite - 1)}
          disabled={seite <= 1}
          className="btn btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Zurück
        </button>
        <button
          type="button"
          onClick={() => wechsle(seite + 1)}
          disabled={seite >= seitenAnzahl}
          className="btn btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Weiter
        </button>
      </div>
    </nav>
  );
};
